/**
 * ReseauCockpitHubPage — Page d'accueil quotidienne du professionnel sur
 * le Réseau Homly.
 *
 * Objectif (Lot UX/UI Réseau — Finition premium) : répondre en moins
 * d'une seconde à la question :
 *     « Quelle est la prochaine action utile pour moi ? »
 *
 * Architecture :
 * - Une zone "À faire maintenant" en haut, ≤ 3 cartes priorisées (focus)
 *   issues du backend hub.
 * - Une checklist d'activation privée et discrète (s'efface au-delà
 *   de 95 %).
 * - Un raccourci permanent « Voir mon profil comme un vendeur ».
 * - Une grille secondaire (4 boucles) pour replonger dans l'activité.
 *
 * Anti-patterns évités :
 * - pas de compteurs de popularité publics ;
 * - pas de fausse urgence (jamais de couleurs rouges ou de pulses) ;
 * - pas de scoring affiché publiquement.
 *
 * data-testid présents pour tests automatisés.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Inbox, MessageSquare, ShieldCheck, FileText, Bell, ExternalLink,
    ChevronRight, Sparkles, Loader2, CheckCircle2, Circle, Eye,
} from 'lucide-react';
import api from '../../lib/api';
import ReseauLayout from '../../components/reseau/ReseauLayout';
import ReseauOnboardingWizard from '../../components/reseau/ReseauOnboardingWizard';
import PremiumTriggerCard from '../../components/reseau/PremiumTriggerCard';
import useAnalyticsEvent from '../../lib/useAnalyticsEvent';

// =============================================================================
// Sub-components
// =============================================================================

function HubSkeleton() {
    return (
        <div className="space-y-6" data-testid="hub-skeleton">
            <div className="h-32 rounded-2xl reseau-card animate-pulse" />
            <div className="grid md:grid-cols-2 gap-4">
                <div className="h-44 rounded-2xl reseau-card animate-pulse" />
                <div className="h-44 rounded-2xl reseau-card animate-pulse" />
            </div>
        </div>
    );
}

function PriorityCard({ priority, onAction }) {
    if (!priority) return null;
    const calm = priority.code === 'share_profile';
    return (
        <button
            type="button"
            onClick={() => onAction(priority)}
            data-testid="cockpit-priority"
            className="w-full text-left p-6 rounded-2xl reseau-card reseau-card-hoverable group"
            style={calm ? {} : { borderLeft: '3px solid var(--reseau-violet)' }}
        >
            <p className="text-xs font-semibold uppercase tracking-wider mb-2"
               style={{ color: 'var(--reseau-violet)' }}>
                {calm ? 'Suggestion du jour' : 'Priorité du jour'}
            </p>
            <h2 className="text-lg font-bold leading-snug mb-1"
                style={{ color: 'var(--reseau-ink)' }}>
                {priority.title}
            </h2>
            <p className="text-sm leading-relaxed mb-4"
               style={{ color: 'var(--reseau-ink-muted)' }}>
                {priority.why}
            </p>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white transition-transform group-hover:translate-x-0.5"
                  style={{ background: 'var(--reseau-violet)' }}
                  data-testid="cockpit-priority-action">
                {priority.cta_label}
                <ChevronRight className="w-4 h-4" />
            </span>
        </button>
    );
}

function DeltaChips({ deltas }) {
    if (!deltas || !deltas.items || deltas.items.length === 0) return null;
    return (
        <div data-testid="cockpit-deltas" className="flex flex-wrap gap-2">
            <span className="text-xs font-medium self-center"
                  style={{ color: 'var(--reseau-ink-muted)' }}>
                Depuis votre dernière visite :
            </span>
            {deltas.items.map((d) => (
                <span key={d.code}
                      data-testid={`delta-${d.code}`}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ background: 'var(--reseau-violet-soft)', color: 'var(--reseau-violet)' }}>
                    {d.label}
                </span>
            ))}
        </div>
    );
}

function VisibilityBlock({ visibility }) {
    if (!visibility) return null;
    const stats = [
        { key: 'views', label: 'Vues du profil (7 j)', value: visibility.profile_views_7d },
        { key: 'appearances', label: 'Apparitions en recherche (7 j)', value: visibility.search_appearances_7d },
        { key: 'clicks', label: 'Clics vers un contact (7 j)', value: visibility.contact_clicks_7d },
    ];
    return (
        <section data-testid="cockpit-visibility" className="reseau-card p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wider mb-4"
                style={{ color: 'var(--reseau-ink-muted)' }}>
                Ma visibilité
            </h2>
            <div className="grid grid-cols-3 gap-3">
                {stats.map((s) => (
                    <div key={s.key} data-testid={`visibility-${s.key}`}>
                        <p className="text-2xl font-extrabold tabular-nums"
                           style={{ color: 'var(--reseau-ink)' }}>
                            {s.value ?? 0}
                        </p>
                        <p className="text-xs leading-tight"
                           style={{ color: 'var(--reseau-ink-muted)' }}>
                            {s.label}
                        </p>
                    </div>
                ))}
            </div>
            <Link to="/reseau/statistiques"
                  className="inline-flex items-center gap-1 mt-4 text-xs font-semibold hover:underline"
                  style={{ color: 'var(--reseau-violet)' }}
                  data-testid="visibility-details-link">
                Comprendre d'où ça vient <ChevronRight className="w-3 h-3" />
            </Link>
        </section>
    );
}

function ActivationCard({ activation, onClickStep }) {
    if (!activation || !activation.show) return null;
    const { percent, essential_percent, essential = [], development = [] } = activation;
    const remaining = [...essential, ...development].filter(s => !s.done);

    const renderStep = (s) => (
        <li key={s.code}
            data-testid={`activation-step-${s.code}`}
            className="flex items-start gap-2 text-sm group">
            {s.done
                ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--reseau-violet)' }} />
                : <Circle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--reseau-ink-muted)' }} />
            }
            <button type="button"
                    onClick={() => !s.done && onClickStep(s.code)}
                    className="text-left flex-1"
                    style={{ color: s.done ? 'var(--reseau-ink-muted)' : 'var(--reseau-ink)' }}>
                <span className={s.done ? 'line-through opacity-60' : 'font-medium'}>{s.label}</span>
                {!s.done && s.why && (
                    <span className="block text-xs mt-0.5"
                          style={{ color: 'var(--reseau-ink-muted)' }}>
                        {s.why}
                    </span>
                )}
            </button>
        </li>
    );

    return (
        <section data-testid="activation-card" className="reseau-card p-5">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h2 className="text-sm font-semibold mb-0.5" style={{ color: 'var(--reseau-ink)' }}>
                        Mon activation
                    </h2>
                    <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>
                        Visible uniquement par vous.
                    </p>
                </div>
                <p className="text-2xl font-extrabold tabular-nums"
                   style={{ color: 'var(--reseau-violet)' }}
                   data-testid="activation-percent">
                    {percent}%
                </p>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden mb-5"
                 style={{ background: 'rgba(104,23,147,0.08)' }}>
                <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                        width: `${percent}%`,
                        background: 'linear-gradient(90deg,#681793,#FF914D)',
                    }}
                />
            </div>

            {essential.length > 0 && (
                <div className="mb-4" data-testid="activation-essential">
                    <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                       style={{ color: 'var(--reseau-violet)' }}>
                        Essentiel ({essential_percent}%)
                    </p>
                    <ul className="space-y-2.5">
                        {essential.map(renderStep)}
                    </ul>
                </div>
            )}

            {development.length > 0 && (
                <div data-testid="activation-development">
                    <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                       style={{ color: 'var(--reseau-ink-muted)' }}>
                        Développement
                    </p>
                    <ul className="space-y-2.5">
                        {development.map(renderStep)}
                    </ul>
                </div>
            )}

            {remaining.length > 0 && (
                <p className="text-xs mt-4 pt-3 border-t"
                   style={{ color: 'var(--reseau-ink-muted)', borderColor: 'rgba(26,18,36,0.06)' }}>
                    Plus que {remaining.length} étape{remaining.length > 1 ? 's' : ''} pour une présence Homly complète.
                </p>
            )}
        </section>
    );
}

function LoopSummary({ loops, profile, onTrack }) {
    const items = [
        {
            key: 'intents',
            icon: Inbox,
            label: 'Demandes vendeurs',
            value: loops.operational.intents_open,
            sub: loops.operational.intents_overdue > 0
                ? `${loops.operational.intents_overdue} > 24h`
                : 'À jour',
            to: '/reseau/demandes-vendeurs',
            testid: 'loop-intents',
        },
        {
            key: 'reviews',
            icon: MessageSquare,
            label: 'Avis vendeurs',
            value: loops.reputation.reviews_to_respond,
            sub: loops.reputation.invitations_pending > 0
                ? `${loops.reputation.invitations_pending} invit. en attente`
                : 'Aucune invitation en cours',
            to: '/reseau/avis-vendeurs',
            testid: 'loop-reviews',
        },
        {
            key: 'proofs',
            icon: ShieldCheck,
            label: 'Ma réputation',
            value: loops.reputation.proofs_to_review,
            sub: loops.reputation.proofs_to_review > 0
                ? 'Preuves à examiner'
                : 'Tout est validé',
            to: '/reseau/ma-reputation',
            testid: 'loop-proofs',
        },
        {
            key: 'feed',
            icon: FileText,
            label: 'Activité réseau',
            value: loops.network.unread_notifications,
            sub: loops.network.days_since_last_post == null
                ? 'Aucune publication encore'
                : loops.network.days_since_last_post < 7
                    ? `Dernière publication il y a ${loops.network.days_since_last_post}j`
                    : `Plus de ${loops.network.days_since_last_post}j sans publication`,
            to: '/reseau/fil',
            testid: 'loop-feed',
        },
    ];
    return (
        <section className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--reseau-ink-muted)' }}>
                    Mes boucles
                </h2>
                {profile?.public_url && (
                    <Link
                        to={profile.public_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => onTrack('public_profile_viewed', { source: 'cockpit' })}
                        className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                        style={{ color: 'var(--reseau-violet)' }}
                        data-testid="link-view-as-seller"
                    >
                        <Eye className="w-4 h-4" />
                        Voir mon profil comme un vendeur
                        <ExternalLink className="w-3 h-3" />
                    </Link>
                )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                {items.map(it => {
                    const Icon = it.icon;
                    return (
                        <Link
                            key={it.key}
                            to={it.to}
                            data-testid={it.testid}
                            className="block p-4 rounded-2xl reseau-card reseau-card-hoverable"
                        >
                            <div className="flex items-start gap-3">
                                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                                     style={{ background: 'rgba(104,23,147,0.08)', color: 'var(--reseau-violet)' }}>
                                    <Icon className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium"
                                       style={{ color: 'var(--reseau-ink-muted)' }}>
                                        {it.label}
                                    </p>
                                    <p className="text-2xl font-extrabold tabular-nums"
                                       style={{ color: 'var(--reseau-ink)' }}>
                                        {it.value}
                                    </p>
                                    <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>
                                        {it.sub}
                                    </p>
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </section>
    );
}

// =============================================================================
// Page
// =============================================================================

export default function ReseauCockpitHubPage() {
    const [hub, setHub] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const track = useAnalyticsEvent();

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const [me, h] = await Promise.all([
                    api.get('/auth/me'),
                    api.get('/reseau/cockpit/hub'),
                ]);
                if (cancelled) return;
                setUser(me.data);
                setHub(h.data);
                track('cockpit_opened', { focus_count: (h.data.focus || []).length });
            } catch (e) {
                if (!cancelled) {
                    if (e?.response?.status === 401) {
                        navigate('/login?next=/reseau/cockpit');
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFocusAction = (item) => {
        track('focus_action_clicked', { code: item.code, priority: item.priority });
        navigate(item.cta_route);
    };

    const handleActivationStep = (code) => {
        track('activation_step_clicked', { code });
        const routes = {
            identity: '/profile',
            bio: '/profile',
            agency: '/profile',
            zones: '/profile',
            specialties: '/profile',
            first_post: '/reseau/fil?compose=1',
            first_proof: '/reseau/ma-reputation',
            first_invite: '/reseau/avis-vendeurs',
        };
        if (routes[code]) navigate(routes[code]);
    };

    return (
        <ReseauLayout user={user} headerTitle="Cockpit Réseau">
            <ReseauOnboardingWizard enabled={!loading && !!user} />
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6"
                 data-testid="cockpit-hub">
                {loading && <HubSkeleton />}
                {!loading && hub && (
                    <>
                        <header>
                            <div className="flex items-center gap-2 mb-1">
                                <Sparkles className="w-4 h-4" style={{ color: 'var(--reseau-violet)' }} />
                                <h1 className="text-xs font-semibold uppercase tracking-wider"
                                    style={{ color: 'var(--reseau-violet)' }}>
                                    Mon cockpit réseau
                                </h1>
                            </div>
                            <p className="text-sm" style={{ color: 'var(--reseau-ink-muted)' }}>
                                {user?.first_name
                                    ? `Bonjour ${user.first_name}, voici l'essentiel du jour.`
                                    : "Voici l'essentiel du jour."}
                            </p>
                        </header>

                        <DeltaChips deltas={hub.deltas} />

                        <PriorityCard priority={hub.priority} onAction={handleFocusAction} />

                        <PremiumTriggerCard />

                        <ActivationCard activation={hub.activation}
                                        onClickStep={handleActivationStep} />

                        <LoopSummary loops={hub.loops}
                                     profile={hub.profile}
                                     onTrack={track} />

                        <VisibilityBlock visibility={hub.visibility} />

                        <footer className="text-center pt-4">
                            <Link to="/reseau/notifications"
                                  data-testid="link-all-notifications"
                                  className="inline-flex items-center gap-1.5 text-sm hover:underline"
                                  style={{ color: 'var(--reseau-ink-muted)' }}>
                                <Bell className="w-4 h-4" />
                                Voir toutes mes notifications
                            </Link>
                        </footer>
                    </>
                )}
            </div>
        </ReseauLayout>
    );
}
